/**
 * Centralized Data Store for WITAL Application
 * 
 * This store provides a single source of truth for all application data,
 * eliminating the need for scattered API calls throughout components.
 */

import { ref, computed, reactive } from 'vue'
import type { BackendConfig } from '../utils/configService'

// Types for the data store
export interface WeatherData {
  date: string
  city: string
  text: string
  structured_forecast?: any
  attribution?: {
    llm: string
    modelString: string
  }
  cached_at: number
}

export interface LocationData {
      location_name: string
  country_code: string
  country_name?: string
  timezone?: string
  timezone_offset_minutes?: number
  latitude?: number
  longitude?: number
}

export interface TranslationData {
  [key: string]: string
}

// Central data store
class DataStore {
  private static instance: DataStore | null = null

  // Reactive data stores
  public weatherData = reactive<Map<string, WeatherData>>(new Map())
  public config = ref<BackendConfig | null>(null)
  public translations = reactive<Map<string, TranslationData>>(new Map())
  public locationCache = reactive<Map<string, LocationData>>(new Map())

  // Loading states
  public isLoadingConfig = ref(false)
  public isLoadingWeather = ref(false)
  public isLoadingTranslations = ref(false)

  // Track loading state per city/date for lazy loading
  private loadingStates = reactive<Map<string, boolean>>(new Map())

  private constructor() {}

  static getInstance(): DataStore {
    if (!DataStore.instance) {
      DataStore.instance = new DataStore()
    }
    return DataStore.instance
  }

  // Weather data methods
  setWeatherData(key: string, data: WeatherData): void {
    console.log(`📦 DataStore: Storing weather data for ${key}`)
    this.weatherData.set(key, {
      ...data,
      cached_at: Date.now()
    })
  }

  // Lazy loading methods
  setLoadingState(city: string, date: string, isLoading: boolean): void {
    const key = `${city}-${date}`
    if (isLoading) {
      this.loadingStates.set(key, true)
      console.log(`⏳ DataStore: Started loading ${key}`)
    } else {
      this.loadingStates.delete(key)
      console.log(`✅ DataStore: Finished loading ${key}`)
    }
  }

  isLoading(city: string, date: string): boolean {
    const key = `${city}-${date}`
    return this.loadingStates.get(key) || false
  }

  // Clear loading state for a specific city/date
  clearLoadingState(city: string, date: string): void {
    const key = `${city}-${date}`
    this.loadingStates.delete(key)
  }

  getWeatherData(city: string, date: string): WeatherData | null {
    const key = `${city}-${date}`
    const data = this.weatherData.get(key)
    
    if (data) {
      // Check if data is still fresh (24 hours)
      const isStale = Date.now() - data.cached_at > 24 * 60 * 60 * 1000
      if (isStale) {
        console.log(`⚠️ DataStore: Weather data for ${key} is stale`)
        this.weatherData.delete(key)
        return null
      }
    }
    
    return data || null
  }

  hasWeatherData(city: string, date: string): boolean {
    return this.getWeatherData(city, date) !== null
  }

  clearWeatherData(): void {
    console.log('🧹 DataStore: Clearing all weather data')
    this.weatherData.clear()
  }

  // Config methods
  setConfig(config: BackendConfig): void {
    console.log('📦 DataStore: Storing backend config')
    this.config.value = config
  }

  getConfig(): BackendConfig | null {
    return this.config.value
  }

  // Translation methods
  setTranslations(languageCode: string, translations: TranslationData): void {
    console.log(`📦 DataStore: Storing translations for ${languageCode}`)
    this.translations.set(languageCode, translations)
  }

  getTranslations(languageCode: string): TranslationData | null {
    return this.translations.get(languageCode) || null
  }

  hasTranslations(languageCode: string): boolean {
    return this.translations.has(languageCode)
  }

  // Location cache methods
  setLocationData(locationKey: string, data: LocationData): void {
    console.log(`📦 DataStore: Storing location data for ${locationKey}`)
    this.locationCache.set(locationKey, data)
  }

  getLocationData(locationKey: string): LocationData | null {
    return this.locationCache.get(locationKey) || null
  }

  // Computed properties for reactive access
  get weatherEntries() {
    return computed(() => Array.from(this.weatherData.entries()))
  }

  get configLoaded() {
    return computed(() => this.config.value !== null)
  }

  get availableLanguages() {
    return computed(() => Array.from(this.translations.keys()))
  }

  // Debug methods
  getStats() {
    return {
      weatherEntries: this.weatherData.size,
      loadingEntries: this.loadingStates.size,
      configLoaded: !!this.config.value,
      translationLanguages: this.translations.size,
      locationEntries: this.locationCache.size
    }
  }

  clearAll(): void {
    console.log('🧹 DataStore: Clearing all data')
    this.weatherData.clear()
    this.translations.clear()
    this.locationCache.clear()
    this.loadingStates.clear()
    this.config.value = null
  }
}

// Export singleton instance
export const dataStore = DataStore.getInstance()

// Export class for testing
export { DataStore } 